import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { 
  Calculator, 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle, 
  Download,
  RefreshCw,
  BarChart3,
  FileText,
  Settings
} from 'lucide-react';
import { 
  getAMPStats, 
  getAMPConfig, 
  calculateBulkAMP, 
  exportAMPCalculations,
  getProducts 
} from '../../config/api';
import toast from 'react-hot-toast';

const AMPDashboard = () => {
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [calculationOptions, setCalculationOptions] = useState({
    applyMedicaidAdjustment: true,
    apply340BAdjustment: true,
    applyPromptPayDiscount: true,
    startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  // Fetch AMP statistics
  const { data: ampStats, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['ampStats'],
    queryFn: () => getAMPStats(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch AMP configuration
  const { data: ampConfig, isLoading: configLoading } = useQuery({
    queryKey: ['ampConfig'],
    queryFn: getAMPConfig
  });

  // Fetch products for selection
  const { data: productsData, isLoading: productsLoading, error: productsError } = useQuery({
    queryKey: ['products'],
    queryFn: () => getProducts({ limit: 1000 }),
  });

  // Handle products data when loaded
  useEffect(() => {
    if (productsData?.products?.length > 0) {
      setSelectedProducts(productsData.products.map(p => p.id));
    }
  }, [productsData]);

  // Handle products error
  useEffect(() => {
    if (productsError) {
      console.error('Error loading products:', productsError);
    }
  }, [productsError]);

  // Bulk AMP calculation mutation
  const bulkCalculationMutation = useMutation({
    mutationFn: ({ productIds, options }) => calculateBulkAMP(productIds, options),
    onSuccess: (data) => {
      toast.success(data?.message || 'AMP calculation completed');
      refetchStats();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || error.message || 'AMP calculation failed');
    }
  });

  // Export mutation
  const exportMutation = useMutation({
    mutationFn: ({ productIds, options, format }) => exportAMPCalculations(productIds, options, format),
    onSuccess: () => {
      console.log('Export completed successfully');
    },
    onError: (error) => {
      console.error('Export failed:', error);
    }
  });

  const handleProductSelection = (productId, checked) => {
    if (checked) {
      setSelectedProducts(prev => [...prev, productId]);
    } else {
      setSelectedProducts(prev => prev.filter(id => id !== productId));
    }
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === productsData?.products?.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(productsData?.products?.map(p => p.id) || []);
    }
  };

  const handleBulkCalculation = () => {
    if (selectedProducts.length === 0) {
      alert('Please select at least one product');
      return;
    }

    bulkCalculationMutation.mutate({
      productIds: selectedProducts,
      options: calculationOptions
    });
  };

  const handleExport = (format = 'csv') => {
    if (selectedProducts.length === 0) {
      alert('Please select at least one product');
      return;
    }

    exportMutation.mutate({
      productIds: selectedProducts,
      options: calculationOptions,
      format
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  if (statsLoading || configLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">AMP Dashboard</h1>
        <p className="text-gray-600">
          Average Manufacturer Price calculations and compliance monitoring
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Calculator className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Calculations</p>
              <p className="text-2xl font-bold text-gray-900">
                {ampStats?.totalCalculations?.toLocaleString() || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Average AMP</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(ampStats?.averageAMPRatio * 100)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Compliance Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatPercentage(ampStats?.complianceRate)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <BarChart3 className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Rebate Amount</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(ampStats?.totalSavings)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Product Selection */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Product Selection</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={handleSelectAll}
                    className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
                    title={selectedProducts.length === productsData?.products?.length ? 'Deselect all products' : 'Select all products'}
                  >
                    {selectedProducts.length === productsData?.products?.length ? 'Deselect All' : 'Select All'}
                  </button>
                  <button
                    onClick={() => refetchStats()}
                    className="p-1 text-gray-500 hover:text-gray-700"
                    title="Refresh"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="max-h-96 overflow-y-auto">
                {productsLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : productsError ? (
                  <div className="text-center text-red-600">
                    <p>Error loading products: {productsError.message}</p>
                  </div>
                ) : !productsData?.products || productsData.products.length === 0 ? (
                  <div className="text-center text-gray-500">
                    <p>No products found. Please add some products first.</p>
                    <p className="text-sm mt-2">Debug: productsData = {JSON.stringify(productsData, null, 2)}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {productsData?.products?.map((product) => (
                      <div key={product.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          checked={selectedProducts.includes(product.id)}
                          onChange={(e) => handleProductSelection(product.id, e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {product.name}
                          </p>
                          <p className="text-sm text-gray-500">
                            NDC: {product.ndc11} | WAC: {formatCurrency(product.wac)}
                          </p>
                        </div>
                        {product.currentAmp && (
                          <div className="text-sm text-gray-600">
                            AMP: {formatCurrency(product.currentAmp)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  {selectedProducts.length} of {productsData?.products?.length || 0} products selected
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Calculation Options & Actions */}
        <div className="space-y-6">
          {/* Calculation Options */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Calculation Options</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={calculationOptions.applyMedicaidAdjustment}
                    onChange={(e) => setCalculationOptions(prev => ({
                      ...prev,
                      applyMedicaidAdjustment: e.target.checked
                    }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Medicaid Adjustment (23.1%)</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={calculationOptions.apply340BAdjustment}
                    onChange={(e) => setCalculationOptions(prev => ({
                      ...prev,
                      apply340BAdjustment: e.target.checked
                    }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">340B Adjustment (25%)</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={calculationOptions.applyPromptPayDiscount}
                    onChange={(e) => setCalculationOptions(prev => ({
                      ...prev,
                      applyPromptPayDiscount: e.target.checked
                    }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Prompt Pay Discount (2%)</span>
                </label>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Start Date</label>
                <input
                  type="date"
                  value={calculationOptions.startDate}
                  onChange={(e) => setCalculationOptions(prev => ({
                    ...prev,
                    startDate: e.target.value
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">End Date</label>
                <input
                  type="date"
                  value={calculationOptions.endDate}
                  onChange={(e) => setCalculationOptions(prev => ({
                    ...prev,
                    endDate: e.target.value
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Actions</h2>
            </div>
            <div className="p-6 space-y-3">
              <button
                onClick={handleBulkCalculation}
                disabled={bulkCalculationMutation.isPending || selectedProducts.length === 0}
                className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Calculate AMP"
              >
                {bulkCalculationMutation.isPending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Calculator className="h-4 w-4 mr-2" />
                )}
                Calculate AMP
              </button>
              
              <button
                onClick={() => handleExport('csv')}
                disabled={exportMutation.isPending || selectedProducts.length === 0}
                className="w-full flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Export CSV"
              >
                {exportMutation.isPending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Export CSV
              </button>
            </div>
          </div>

          {/* Compliance Status */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Compliance Status</h2>
            </div>
            <div className="p-6">
              <div className="flex items-center space-x-2 mb-4">
                {ampStats?.complianceRate > 90 ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                )}
                <span className="text-sm text-gray-700">
                  {ampStats?.complianceRate > 90 ? 'Compliant' : 'Non-Compliant'}
                </span>
              </div>
              
              <div className="space-y-2 text-sm text-gray-600">
                <p>Products with Issues: {ampStats?.failedCalculations || 0}</p>
                <p>Average Rebate: {formatCurrency(ampStats?.totalSavings / (ampStats?.productsWithAMP || 1))}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Calculations */}
      {bulkCalculationMutation.data && (
        <div className="mt-8 bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Recent Calculation Results</h2>
          </div>
          <div className="p-6">
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">
                    Calculation Completed Successfully
                  </h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>
                      {bulkCalculationMutation.data.data.summary.successful} of {bulkCalculationMutation.data.data.summary.total} products calculated successfully
                    </p>
                    <p>Average AMP: {formatCurrency(bulkCalculationMutation.data.data.summary.averageAMP)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AMPDashboard;
